import { useCallback, useMemo } from "react";
import { useTranslations } from "next-intl";
import { useAppDispatch } from "@lib/redux/hooks";
import { addNotificationToast } from "@features/notifications/notifications-slice";
import { notificationsTools } from "@features/notifications/notifications-tools";
import { NotificationMessagePayload } from "@features/notifications/notifications-types";

export const useNotificationToasts = () => {
  const t = useTranslations("features.notifications.domain.notificationsToasts");
  const dispatch = useAppDispatch();

  const showSuccessToast = useCallback(
    (payload: NotificationMessagePayload) =>
      dispatch(addNotificationToast(notificationsTools.getSuccessNotificationMessage(payload))),
    [dispatch],
  );

  const showErrorToast = useCallback(
    (payload: NotificationMessagePayload) =>
      dispatch(addNotificationToast(notificationsTools.getErrorNotificationMessage(payload))),
    [dispatch],
  );

  const showInfoToast = useCallback(
    (payload: NotificationMessagePayload) =>
      dispatch(addNotificationToast(notificationsTools.getInfoNotificationMessage(payload))),
    [dispatch],
  );

  const showWarningToast = useCallback(
    (payload: NotificationMessagePayload) =>
      dispatch(addNotificationToast(notificationsTools.getWarnNotificationMessage(payload))),
    [dispatch],
  );

  const showSuccessUpdateToast = useCallback(
    (payload?: NotificationMessagePayload) =>
      dispatch(
        addNotificationToast(
          notificationsTools.getSuccessNotificationMessage({
            description: t("updateData.description"),
            ...(payload ?? {}),
          }),
        ),
      ),
    [dispatch, t],
  );

  const showSuccessDeleteToast = useCallback(
    (payload?: NotificationMessagePayload) =>
      dispatch(
        addNotificationToast(
          notificationsTools.getSuccessNotificationMessage({
            description: t("deleteData.description"),
            ...(payload ?? {}),
          }),
        ),
      ),
    [dispatch, t],
  );

  const showSuccessAddToast = useCallback(
    (payload?: NotificationMessagePayload) =>
      dispatch(
        addNotificationToast(
          notificationsTools.getSuccessNotificationMessage({
            description: t("addData.description"),
            ...(payload ?? {}),
          }),
        ),
      ),
    [dispatch, t],
  );

  return useMemo(
    () => ({
      showSuccessToast,
      showSuccessUpdateToast,
      showSuccessDeleteToast,
      showSuccessAddToast,
      showErrorToast,
      showInfoToast,
      showWarningToast,
    }),
    [
      showErrorToast,
      showInfoToast,
      showSuccessAddToast,
      showSuccessDeleteToast,
      showSuccessToast,
      showSuccessUpdateToast,
      showWarningToast,
    ],
  );
};
